import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Navigation, ExternalLink, ChevronLeft, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API_URL, analytics, getTodaysHours, addCacheBust } from "@/lib/utils";
import { getLocationIdFromSlug } from "@/lib/locationUrls";
import {
  getLocationGalleryPhotos,
  getPrimaryPhotoPath,
  getOrderedGalleryPhotos,
} from "@/lib/locationGalleryPhotos";
import { SEO } from "@/components/SEO";
import FreePassPopup from "@/components/layout/FreePassPopup";

const VOORHEES_POOL_SCHEDULE = {
  monday: [
    "7:00 AM - 5:00 PM | Member Swim",
    "5:00 PM - 8:45 PM | SJ Aquatics Club",
    "8:45 PM - Midnight | Member Swim",
  ],
  tuesday: [
    "7:00 AM - 5:00 PM | Member Swim",
    "5:00 PM - 8:30 PM | SJ Aquatics Club",
    "8:30 PM - Midnight | Member Swim",
  ],
  wednesday: [
    "7:00 AM - 5:00 PM | Member Swim",
    "5:00 PM - 8:30 PM | SJ Aquatics Club",
    "8:30 PM - Midnight | Member Swim",
  ],
  thursday: [
    "7:00 AM - 5:00 PM | Member Swim",
    "5:00 PM - 8:30 PM | SJ Aquatics Club",
    "8:30 PM - Midnight | Member Swim",
  ],
  friday: ["7:00 AM - Midnight | Member Swim"],
  saturday: [
    "7:00 AM - 8:30 AM | SJ Aquatics Club",
    "8:30 AM - 10:30 AM | Member Swim",
    "10:30 AM - 12:30 PM | SJ Aquatics Club",
    "12:30 PM - Midnight | Member Swim",
  ],
  sunday: [
    "8:00 AM - 12:30 PM | SJ Aquatics Club",
    "7:00 AM - Midnight | Member Swim",
  ],
};

const VOORHEES_POOL_NOTE = `*The pool is reserved 8:00 AM - 12:00 PM on the following Sundays for South Jersey Scuba classes.

April 12, 19
May 3, 17
June 7, 14
July 12, 19
August 9, 16
September 13, 20
October 11, 18
November 8, 15
December 6, 13`;

function PhotoGrid({ photos, locationName, onSelect }) {
  if (!photos || photos.length === 0) return null;
  return (
    <div data-testid="photo-gallery">
      <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4">PHOTO GALLERY</h2>
      <div className="gallery-grid">
        {photos.map((p, i) => (
          <div key={p} className="gallery-item cursor-pointer" onClick={() => onSelect(p)} data-testid={`gallery-image-${i}`}>
            <img
              src={addCacheBust(p)}
              alt={`${locationName} gym interior - photo ${i + 1}`}
              loading="lazy"
              onError={(e) => {
                // Fallback to placeholder if image fails to load
                e.target.src = "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800";
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function AmenitiesGrid({ amenities }) {
  if (!amenities || amenities.length === 0) return null;
  return (
    <div data-testid="amenities-section">
      <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4">AMENITIES</h2>
      <div className="amenities-grid">
        {amenities.map((a, i) => (
          <div key={i} className="flex items-center gap-3 p-3 bg-gray-100 border border-gray-200 rounded-lg" data-testid={`amenity-${i}`}>
            <Check className="w-5 h-5 text-brand-pink flex-shrink-0" />
            <span className="text-gray-800">{a}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function HoursTable({ hours, title }) {
  if (!hours || hours.length === 0) return null;
  return (
    <div className="bg-gray-100 border border-gray-200 rounded-lg p-6" data-testid="hours-section">
      <h2 className="text-xl font-heading font-bold text-gray-900 mb-4">{title || "HOURS"}</h2>
      <table className="hours-table">
        <tbody>
          {hours.map((h, i) => (
            <tr key={i}>
              <td className="day">{h.day}</td>
              <td className="hours">
                {h.is_closed ? <span className="text-red-400">Closed</span> 
                  : h.open === "24 Hours" ? <span className="font-semibold text-orange-500">24 Hours</span> 
                  : <span className="text-slate-600">{`${h.open} - ${h.close}`}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PoolScheduleTable({ poolHours, note }) {
  if (!poolHours || typeof poolHours !== "object") return null;

  const dayOrder = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
  const dayLabel = {
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    friday: "Friday",
    saturday: "Saturday",
    sunday: "Sunday",
  };

  const rows = dayOrder.filter((day) => Array.isArray(poolHours[day]) && poolHours[day].length > 0);
  if (rows.length === 0) return null;

  const getSlotStyles = (slot) => {
    if (slot.includes("SJ Aquatics Club")) {
      return {
        chip: "bg-blue-50 border-blue-200",
        text: "text-blue-700",
      };
    }
    return {
      chip: "bg-orange-50 border-orange-200",
      text: "text-orange-700",
    };
  };

  return (
    <div className="bg-gray-100 border border-gray-200 rounded-lg p-6" data-testid="pool-hours-section">
      <h2 className="text-xl font-heading font-bold text-gray-900 mb-4">Pool Schedule</h2>
      <div className="space-y-4">
        {rows.map((day) => (
          <div key={day} className="border border-gray-200 rounded-md bg-white p-4">
            <h3 className="font-semibold text-gray-900 mb-3">{dayLabel[day]}</h3>
            <div className="space-y-2">
              {poolHours[day].map((slot, idx) => {
                const [timeRange, activity] = slot.split("|").map((s) => s.trim());
                const styles = getSlotStyles(slot);
                return (
                  <div key={`${day}-${idx}`} className={`rounded-md border px-3 py-2 ${styles.chip}`}>
                    <p className={`text-sm sm:text-base ${styles.text}`}>
                      <span className="font-medium">{timeRange}</span>
                      {activity ? <span> | {activity}</span> : null}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {note ? (
        <p className="text-sm italic text-blue-700 mt-4 border-t border-gray-200 pt-3 whitespace-pre-line">
          {note}
        </p>
      ) : null}
    </div>
  );
}

export default function LocationDetailPage() {
  const { locationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const locationPath = location.pathname;
  const [locationData, setLocationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImg, setSelectedImg] = useState(null);

  useEffect(() => {
    // Handle both old format (/locations/:id) and new format (/:slug)
    let actualLocationId = locationId;
    
    if (locationPath.startsWith("/locations/")) {
      // Old format: /locations/:id - use locationId from params
      actualLocationId = locationId;
    } else if (!locationId) {
      // New format: /:slug - extract slug from path (no :locationId param)
      const slug = locationPath.replace(/^\/|\/$/g, "");
      actualLocationId = getLocationIdFromSlug(slug);
    }
    
    if (!actualLocationId) {
      setIsLoading(false);
      return;
    }
    
    const url = `${API_URL}/locations/${actualLocationId}`;
    axios.get(url)
      .then(res => {
        setLocationData(res.data);
        analytics.trackLocationSelect(res.data.id, res.data.name);
      })
      .catch(err => console.error("Failed:", err))
      .finally(() => setIsLoading(false));
  }, [locationId, locationPath]);

  const { galleryPhotos, orderedGalleryPhotos } = useMemo(() => {
    if (!locationData) return { galleryPhotos: [], orderedGalleryPhotos: [] };
    const gp = getLocationGalleryPhotos(locationData.id, locationData.photos);
    const ordered = getOrderedGalleryPhotos(locationData.id, locationData.photos, gp);
    return { galleryPhotos: gp, orderedGalleryPhotos: ordered };
  }, [locationData]);

  if (isLoading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <SEO
          title="Loading Location"
          description="Loading Jersey Girls location information..."
          url={locationPath}
          noindex={true}
        />
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-pink" />
      </div>
    );
  }

  if (!locationData) {
    return (
      <div className="min-h-screen pt-20 flex flex-col items-center justify-center" data-testid="location-not-found">
        <SEO
          title="Location Not Found"
          description="The requested Jersey Girls location could not be found. Browse all our locations."
          url={locationPath}
          noindex={true}
        />
        <MapPin className="w-16 h-16 text-brand-pink mb-4" />
        <h2 className="text-2xl font-heading text-gray-900 mb-2">Location Not Found</h2>
        <Link to="/locations"><Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50">Back</Button></Link>
      </div>
    );
  }

  const heroPath = getPrimaryPhotoPath(locationData.id, locationData.photos, galleryPhotos);
  const heroImg = heroPath
    ? addCacheBust(heroPath)
    : "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1920";
  const handleSignup = () => {
    analytics.trackSignupClick(locationData.id, locationData.name);
    navigate("/join-thank-you");
  };

  const locationImage = heroPath
    ? `https://jerseygirlsclubs.com${heroPath}`
    : "https://jerseygirlsclubs.com/images/Jersey-Girls-Logo-768x671.jpeg";
  const isVoorhees = locationData.id === "giant-fitness-voorhees";
  const poolHours = isVoorhees ? VOORHEES_POOL_SCHEDULE : locationData.department_hours?.pool;
  const poolScheduleNote = isVoorhees ? VOORHEES_POOL_NOTE : locationData.pool_schedule_note;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": ["HealthClub", "LocalBusiness"],
    "name": locationData.name,
    "description": `${locationData.name} - ${locationData.address}, ${locationData.city}, ${locationData.state}. Premium fitness center with ${locationData.amenities?.join(", ") || "state-of-the-art equipment"}.`,
    "url": `https://jerseygirlsclubs.com${locationPath}`,
    "image": locationImage,
    "telephone": locationData.phone,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": locationData.address,
      "addressLocality": locationData.city,
      "addressRegion": locationData.state,
      "postalCode": locationData.zip_code,
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": locationData.latitude,
      "longitude": locationData.longitude
    },
    "areaServed": {
      "@type": "City",
      "name": locationData.city
    },
    "openingHoursSpecification": locationData.hours?.map(h => ({
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": h.day,
      "opens": h.open,
      "closes": h.close
    })) || [],
    "priceRange": "$$",
    "amenityFeature": locationData.amenities?.map(a => ({
      "@type": "LocationFeatureSpecification",
      "name": a,
      "value": true
    })) || []
  };

  return (
    <div className="min-h-screen pt-20" data-testid="location-detail-page">
      <FreePassPopup />
      <SEO
        title={locationData.name}
        description={`${locationData.name} - ${locationData.address}, ${locationData.city}, ${locationData.state}. Premium fitness center with ${locationData.amenities?.slice(0, 3).join(", ") || "state-of-the-art equipment"}. ${locationData.is_women_only ? "Women-only facility." : ""} Sign up today!`}
        url={locationPath}
        image={locationImage}
        structuredData={structuredData}
      />
      <section className="relative h-64 md:h-96">
            <img 
              src={heroImg}
              alt={`${locationData.name} fitness center`}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to placeholder if image fails to load
                e.target.src = "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1920";
              }}
            />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/50 to-transparent" />
        <div className="absolute top-3 left-3">
          <Link to="/locations"><Button variant="outline" className="h-11 px-4 bg-black/50 border-white/20 text-white"><ChevronLeft className="w-4 h-4 mr-1" />All Locations</Button></Link>
        </div>
        {locationData.is_women_only && <div className="absolute top-4 right-4 women-only-badge">Women Only</div>}
      </section>

      <section className="px-4 md:px-8 -mt-20 relative z-10 pb-12">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="bg-gray-100 border border-gray-200 rounded-lg p-6 md:p-8 mb-8">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                <div>
                  <h1 className="text-3xl md:text-5xl font-heading font-black text-gray-900 mb-4" data-testid="location-name">{locationData.name}</h1>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 text-gray-700"><MapPin className="w-5 h-5 text-brand-pink mt-0.5" /><span>{locationData.address}, {locationData.city}, {locationData.state} {locationData.zip_code}</span></div>
                    <div className="flex items-center gap-3 text-gray-700"><Phone className="w-5 h-5 text-brand-pink" /><a href={`tel:${locationData.phone}`} className="hover:text-brand-pink">{locationData.phone}</a></div>
                    <div className="flex items-center gap-3 text-gray-700"><Clock className="w-5 h-5 text-brand-pink" /><span>{locationData.hours_note || `Today: ${getTodaysHours(locationData.hours)}`}</span></div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:min-w-[200px]">
                  <a href={locationData.signup_url || "#"} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto lg:w-full" onClick={(e) => { if (!locationData.signup_url) e.preventDefault(); handleSignup(); }}>
                    <Button size="lg" type="button" className="w-full bg-brand-pink hover:bg-brand-pink-hover text-white font-bold uppercase" data-testid="signup-online-btn">Sign Up Online<ExternalLink className="w-4 h-4 ml-2" /></Button>
                  </a>
                  <a href={locationData.directions_url} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto lg:w-full"><Button size="lg" variant="outline" className="w-full border-gray-300 text-gray-700 hover:bg-gray-50" data-testid="get-directions-btn"><Navigation className="w-4 h-4 mr-2" />Get Directions</Button></a>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-4 lg:gap-8">
              <div className="lg:col-span-2 space-y-8">
                <PhotoGrid photos={orderedGalleryPhotos} locationName={locationData.name} onSelect={setSelectedImg} />
                <AmenitiesGrid amenities={locationData.amenities} />
              </div>
              <div className="space-y-8">
                <HoursTable hours={locationData.hours} title={locationData.hours_note} />
                <PoolScheduleTable
                  poolHours={poolHours}
                  note={poolScheduleNote}
                />
                <div className="bg-gray-100 border border-gray-200 rounded-lg p-6" data-testid="quick-links">
                  <h2 className="text-xl font-heading font-bold text-gray-900 mb-4">QUICK LINKS</h2>
                  <div className="space-y-2">
                    <Link to="/contact-us" className="block p-3 text-gray-700 hover:text-brand-pink rounded">Contact Us</Link>
                    <Link to="/feedback" className="block p-3 text-gray-700 hover:text-brand-pink rounded">Leave Feedback</Link>
                    <Link to="/cancel" className="block p-3 text-gray-700 hover:text-brand-pink rounded">Cancellation Request</Link>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {selectedImg && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setSelectedImg(null)}>
          <img 
            src={addCacheBust(selectedImg)} 
            alt="Gallery" 
            className="max-w-full max-h-full rounded"
            onError={(e) => {
              e.target.src = "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1200";
            }}
          />
        </div>
      )}
    </div>
  );
}
